/**
 * Course-specific helper functions
 * Updated to use SCORM tracking instead of user_score
 */

import { formatDate, formatDuration, getStatusBadgeVariant, getStatusIcon, getSampleImage } from './helpers';

// Module status helpers using SCORM tracking
export const getModuleStatus = (moduleId, scormTracking = {}) => {
  // Check if there's SCORM tracking data for this module
  // scormTracking can be either a single tracking object or a map
  let trackingData = null;
  
  if (scormTracking.module_id === moduleId) {
    // Direct tracking object
    trackingData = scormTracking;
  } else if (scormTracking[moduleId]) {
    // Tracking map with moduleId as key
    trackingData = scormTracking[moduleId];
  }
  
  if (trackingData && trackingData.lesson_status) {
    const status = trackingData.lesson_status;
    switch (status) {
      case 'completed':
      case 'passed':
        return 'Completed';
      case 'incomplete':
        return 'In Progress';
      case 'failed':
        return 'Failed';
      case 'not_attempted':
      default:
        return 'Not Started';
    }
  }
  return 'Not Started';
};

// Course progress calculation using SCORM tracking
export const calculateCourseProgress = (modules, scormTrackingArray = []) => {
  if (modules.length === 0) return 0;
  
  const completedModules = modules.filter(module => {
    const tracking = scormTrackingArray.find(t => t.module_id === module.id);
    return tracking && ['completed', 'passed'].includes(tracking.lesson_status);
  }).length;
  
  return Math.round((completedModules / modules.length) * 100);
};

// Module sorting
export const sortModulesBySequence = (modules) => {
  return modules.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
};

// SCORM tracking mapping
export const mapScormTracking = (trackingArray = []) => {
  const trackingMap = {};
  trackingArray.forEach(tracking => {
    // Map by module_id (can be number or string)
    const moduleId = tracking.module_id;
    if (moduleId !== null && moduleId !== undefined) {
      // Use both string and number keys for flexibility
      trackingMap[moduleId] = tracking;
      trackingMap[String(moduleId)] = tracking;
      if (typeof moduleId === 'string') {
        trackingMap[Number(moduleId)] = tracking;
      }
    }
  });
  return trackingMap;
};

// Module card data preparation using SCORM tracking
export const prepareModuleCardData = (module, index, scormTracking = {}) => {
  const status = getModuleStatus(module.id, scormTracking);
  const tracking = scormTracking[module.id];
  
  return {
    ...module,
    status,
    scormTracking: tracking,
    sequenceNumber: index + 1,
    badgeVariant: getStatusBadgeVariant(status),
    statusIcon: getStatusIcon(status),
    sampleImage: module.module_image || getSampleImage(module.name),
    formattedDuration: formatDuration(module.duration),
    hasScore: tracking && tracking.score_raw !== null && tracking.score_raw !== undefined,
    hasStartDate: tracking && tracking.first_access_date,
    hasEndDate: tracking && tracking.completion_date,
    hasTimeSpent: tracking && tracking.total_time && tracking.total_time !== '00:00:00' && tracking.total_time !== '',
    hasBadge: false, // SCORM doesn't have badge concept
    score: tracking ? tracking.score_raw : null,
    progress: tracking ? Math.round((tracking.progress_measure || 0) * 100) : 0,
    timeSpent: tracking ? tracking.total_time : null,
    formattedStartDate: tracking?.first_access_date ? formatDate(tracking.first_access_date) : null,
    formattedEndDate: tracking?.completion_date ? formatDate(tracking.completion_date) : null,
  };
};

// Course header data preparation using SCORM tracking
export const prepareCourseHeaderData = (course, modules, scormTrackingArray = []) => {
  const progress = calculateCourseProgress(modules, scormTrackingArray);
  
  return {
    title: course?.title || course?.name || 'Course Modules',
    description: course?.description || '',
    moduleCount: modules.length,
    progress,
    progressText: `${progress}% Complete`,
  };
};

// Validation helpers
export const validateModuleUrl = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const validateCourseId = (courseId) => {
  return courseId && courseId !== 'undefined' && courseId !== 'null';
};

// Filter helpers using SCORM tracking
export const filterModulesByStatus = (modules, scormTrackingArray = [], status) => {
  return modules.filter(module => {
    const tracking = scormTrackingArray.find(t => t.module_id === module.id);
    return getModuleStatus(module.id, tracking) === status;
  });
};

export const filterModulesBySearch = (modules, searchTerm) => {
  if (!searchTerm) return modules;
  const term = searchTerm.toLowerCase();
  return modules.filter(module => 
    module.name.toLowerCase().includes(term) || 
    module.description.toLowerCase().includes(term)
  );
};

// Export all the helper functions from the main helpers file
export {
  formatDate,
  formatDuration,
  getStatusBadgeVariant,
  getStatusIcon,
  getSampleImage,
}; 